import { ChangeDetectionStrategy, Component, computed, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { FikoYTKokBilesenComponent } from '@/src/service/kok-bilesen/kok-bilesen.component';
import { OzelAlanDuzenleyiciComponent } from '@service/fikoyt-ui/src/lib/ozel-alan/ozel-alan-duzenleyici.component';
import { IOzelAlan } from '@service/fikoyt-ui/src/lib/ozel-alan/ozel-alan.types';

// ---------------------------------------------------------------------------
// genel-stok edit (paylaş kutusu): kullanıcı isim/birim/limit girer ve
// istediği kadar "Özel Alan" (ad: değer) ekleyip herhangi bir ürünü takip
// edebilir.
//
// VERİ YAPISI:
//   parametreler() = { kart, icerik, ek }
//   Gerçek veri: params.icerik.ek  (params.ek aynı verinin kopyası)
//   ek.parametreler[] = sistem tanım dizisi — [{ismi, baslik, turu, degeri}]
//     → isim/birim/limit/notlar buradan okunur/yazılır (degeri alanı)
//   ek.ozelAlanlar[] = kullanıcı dinamik alanları [{ad, deger}]
//     → buradan okunur/yazılır, ek.parametreler ile KARISTIRILMAMALI
//
// YAZMA KURALI: her zaman setEkGuncelle() ile deep-clone + üç kopya senkron
// ---------------------------------------------------------------------------
@Component({
  selector: 'genel-stok-edit',
  standalone: true,
  imports: [FormsModule, NzInputModule, NzButtonModule, NzIconModule, OzelAlanDuzenleyiciComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ge">
      <div class="ge-row">
        <div class="ge-alan" style="flex:1">
          <label class="ge-etiket">İsim</label>
          <input nz-input nzSize="small"
            [ngModel]="paramDeger('isim')"
            (ngModelChange)="paramGuncelle('isim', $event)"
            placeholder="Ör: Vida M6x20"/>
        </div>
        <div class="ge-alan" style="flex:0 0 100px">
          <label class="ge-etiket">Birim</label>
          <input nz-input nzSize="small"
            [ngModel]="paramDeger('birim') || 'adet'"
            (ngModelChange)="paramGuncelle('birim', $event)"
            placeholder="adet"/>
        </div>
      </div>

      <div class="ge-row">
        <div class="ge-alan">
          <label class="ge-etiket">Uyarı Limiti</label>
          <input nz-input nzSize="small" type="number"
            [ngModel]="paramDeger('uyariLimit')"
            (ngModelChange)="paramGuncelle('uyariLimit', $event)"
            placeholder="Ör: 30"/>
          <span class="ge-aciklama">Bu miktarın altına inince "Uyarı" rozeti gösterilir</span>
        </div>
        <div class="ge-alan">
          <label class="ge-etiket">Kritik Limit</label>
          <input nz-input nzSize="small" type="number"
            [ngModel]="paramDeger('kritikLimit')"
            (ngModelChange)="paramGuncelle('kritikLimit', $event)"
            placeholder="Ör: 15"/>
          <span class="ge-aciklama">Bu miktarın altına inince "Kritik" rozeti gösterilir</span>
        </div>
      </div>

      <ozel-alan-duzenleyici
        [alanlar]="ozelAlanlar()"
        (alanlarDegisti)="ozelAlanlarKaydet($event)" />

      <div class="ge-row">
        <div class="ge-alan" style="flex:1">
          <label class="ge-etiket">Notlar</label>
          <input nz-input nzSize="small"
            [ngModel]="paramDeger('notlar') || ''"
            (ngModelChange)="paramGuncelle('notlar', $event)"
            placeholder="Kısa not yazın..."/>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .ge { display:flex; flex-direction:column; gap:8px; }
    .ge-row { display:flex; gap:10px; }
    .ge-alan { display:flex; flex-direction:column; gap:2px; flex:1; padding:4px 0; }
    .ge-etiket { font-weight:500; font-size:12px; color:var(--py-metin-ucuncul, rgba(0,0,0,.45)); letter-spacing:-0.1px; }
    .ge-aciklama { font-size:11px; color:var(--py-metin-devre-disi, rgba(0,0,0,.25)); margin-top:1px; }

  `],
})
export class GenelStokEditComponent extends FikoYTKokBilesenComponent {
  parametreler = model<any>();
  constructor() { super(); }

  // ── Okuma ────────────────────────────────────────────────────────────────

  /** Aktif ek objesi */
  p = computed(() => this.parametreler()?.icerik?.ek || this.parametreler()?.ek || {});

  /** ek.parametreler[] dizisinden ismi'ye göre degeri döndürür */
  paramDeger(ismi: string): any {
    const liste: any[] = this.p()?.parametreler;
    if (!Array.isArray(liste)) return undefined;
    return liste.find(p => p.ismi === ismi)?.degeri;
  }

  /** Kullanıcı dinamik alanları: ek.ozelAlanlar[] — tür bazlı (metin/sayı/tarih/onay/seçim/renk) */
  ozelAlanlar = computed<IOzelAlan[]>(() => {
    const v = this.p()?.ozelAlanlar;
    return Array.isArray(v) ? v : [];
  });

  // ── Yazma altyapısı ──────────────────────────────────────────────────────

  /**
   * Deep-clone + üç kopya senkron güncelleme.
   * Angular OnPush signal sistemi referans eşitliği (===) ile çalışır;
   * shallow spread ({ ...params }) iç objelerin referansını değiştirmez,
   * computed'lar tetiklenmez. JSON clone tüm referansları kırar.
   */
  private setEkGuncelle(params: any, fn: (ek: any) => void): any {
    const yeni = JSON.parse(JSON.stringify(params));
    const ek = yeni?.icerik?.ek ?? yeni?.ek;
    if (!ek) return yeni;
    fn(ek);
    if (yeni.icerik?.ek)                     yeni.icerik.ek = ek;
    if (yeni.ek)                              yeni.ek = ek;
    if (yeni.kart?.veri?.icerikler?.[0]?.ek) yeni.kart.veri.icerikler[0].ek = ek;
    return yeni;
  }

  // ── Parametre (isim/birim/limit/notlar) ──────────────────────────────────

  /**
   * ek.parametreler[i].degeri'ni günceller.
   * Parametre bulunamazsa sessizce geçer.
   */
  paramGuncelle(ismi: string, deger: any) {
    const params = this.parametreler();
    if (!params) return;
    this.parametreler.set(this.setEkGuncelle(params, ek => {
      if (!Array.isArray(ek.parametreler)) return;
      const p = ek.parametreler.find((x: any) => x.ismi === ismi);
      if (p) p.degeri = deger;
    }));
    this.CD.detectChanges();
  }

  // ── Özel Alanlar (ek.ozelAlanlar[]) ─────────────────────────────────────
  // ozel-alan-duzenleyici bileşeni her değişiklikte TAM yeni diziyi verir;
  // burada tek işimiz deep-clone + üç kopya senkron yazmak.

  ozelAlanlarKaydet(yeniListe: IOzelAlan[]) {
    const params = this.parametreler();
    if (!params) return;
    this.parametreler.set(this.setEkGuncelle(params, ek => {
      ek.ozelAlanlar = yeniListe;
    }));
    this.CD.detectChanges();
  }
}
